/**
 * PR13-C (Visual Feedback V2): the headline/skill lanes' shared priority
 * queue. Deliberately framework-free (no Phaser/DOM access here, same
 * split as `src/qa/QaMode.ts`) so the "important events replace/defer
 * low-priority feedback, low-priority events never erase a major result"
 * rule is a plain, directly-testable data structure - `GameScene` has no
 * unit test harness of its own (see that file's doc), so anything that
 * genuinely needs verifying lives here instead.
 *
 * One `FeedbackQueue` instance per lane (headline lane, Skill lane) - each
 * lane only ever shows one item at a time, deciding for itself whether a
 * newly-arriving item pre-empts the current one or waits behind it.
 */

/** Fixed priority tiers, highest first - see the PR13-C spec's "Recommended priority" list. Two items may share a tier (e.g. every CHAOS reveal stage); a later item at the same tier still pre-empts an earlier one still showing, which is what lets a chained sequence (`enqueueChain`) advance through its own stages. */
export const FeedbackPriority = {
  /** Death, gate entry, level completion - the run just ended or is ending; nothing may replace this except another item at the same tier. */
  runEnd: 100,
  /** STARFRUIT gate opening, and every stage of a CHAOS reveal (anticipation, then the resolved JACKPOT!/WORMHOLE!/SHRINK!/SPEED TRIAL!/SCORE HIT!). */
  majorEvent: 90,
  /** WORMHOLE/SPEED TRIAL activation and WORMHOLE REFRESHED - real-time status changes, not tied to a single bite. */
  statusActivation: 80,
  /** Reserved distinct tier for JACKPOT/SHRINK per the spec's priority list - currently always folded into `majorEvent` (both are CHAOS outcomes, shown as part of the same reveal chain), kept as its own named constant so a future standalone JACKPOT/SHRINK source has an obvious place to slot in without renumbering anything else. */
  outcomeDetail: 70,
  /** Skill/Clutch: geometric maneuvers, FLOW, TRICK/CLUTCH BITE, WORMHOLE BITE/CLUTCH. */
  skill: 60,
  rush: 40,
  safe: 20,
} as const;

export interface QueuedFeedback<T> {
  /** Caller-defined identity for the item (e.g. a headline kind) - never read by the queue itself, only carried through for the consumer. */
  readonly payload: T;
  readonly priority: number;
  readonly holdMs: number;
}

/** What `enqueue()` actually did with the item - purely informational, useful for tests and for a caller that wants to know whether its item is visible yet. */
export type EnqueueOutcome = "shown-immediately" | "queued" | "dropped";

/**
 * A single-lane priority queue: at most one item "active" (currently
 * shown) at a time, with the rest waiting in `pending`, highest priority
 * first. A new item at or above the active item's priority pre-empts it
 * immediately; a lower-priority item waits. `pending` is capped
 * (`maxQueueLength`) - overflow drops the lowest-priority waiting item(s),
 * never the active one, so a burst of low-priority events (e.g. rapid
 * SAFE/RUSH bites) can never grow this without bound.
 */
export class FeedbackQueue<T> {
  private active: QueuedFeedback<T> | null = null;
  private activeRemainingMs = 0;
  private pending: QueuedFeedback<T>[] = [];

  constructor(private readonly maxQueueLength: number = 6) {}

  get current(): QueuedFeedback<T> | null {
    return this.active;
  }

  get currentRemainingMs(): number {
    return this.activeRemainingMs;
  }

  get pendingCount(): number {
    return this.pending.length;
  }

  /** A plain new item - not part of a chained sequence with anything already queued. See the class doc for the pre-empt/queue/drop rule. */
  enqueue(item: QueuedFeedback<T>): EnqueueOutcome {
    if (!this.active || item.priority >= this.active.priority) {
      this.setActive(item);
      return "shown-immediately";
    }
    this.pending.push(item);
    this.pending.sort((a, b) => b.priority - a.priority);
    if (this.pending.length > this.maxQueueLength) {
      this.pending.length = this.maxQueueLength;
      return "dropped";
    }
    return "queued";
  }

  /**
   * A fixed, ordered sequence that is all "one story" (e.g. CHAOS's
   * anticipation stage followed by its resolved outcome) - the first item
   * goes through the normal pre-empt/queue decision above, but every
   * later stage always plays immediately after it, ahead of anything else
   * already waiting, so the sequence can never be split apart by an
   * unrelated item of equal priority landing in between.
   */
  enqueueChain(items: readonly QueuedFeedback<T>[]): void {
    if (items.length === 0) return;
    const [first, ...rest] = items;
    if (!this.active || first.priority >= this.active.priority) {
      this.setActive(first);
    } else {
      this.pending.push(first);
      this.pending.sort((a, b) => b.priority - a.priority);
    }
    this.pending = [...rest, ...this.pending];
    if (this.pending.length > this.maxQueueLength) this.pending.length = this.maxQueueLength;
  }

  private setActive(item: QueuedFeedback<T>): void {
    this.active = item;
    this.activeRemainingMs = item.holdMs;
  }

  /**
   * Advance the active item's remaining hold time by `dtMs` - only ever
   * call this while the owning scene is actually running (never while
   * Pause holds the frame), the same "simply stop calling it" freeze
   * pattern the engine's own real-time countdowns use. Returns true the
   * instant the active item's hold expires and the next one (if any)
   * takes over.
   */
  tick(dtMs: number): boolean {
    if (!this.active) return false;
    this.activeRemainingMs -= dtMs;
    if (this.activeRemainingMs > 0) return false;
    this.advance();
    return true;
  }

  private advance(): void {
    const next = this.pending.shift();
    if (next) this.setActive(next);
    else this.active = null;
  }

  /** Full reset - death, Restart, gate entry, scene shutdown. Never leaves a stale active/pending item that could resurface on the next run. */
  clear(): void {
    this.active = null;
    this.activeRemainingMs = 0;
    this.pending = [];
  }
}
